import PropTypes from 'prop-types';
import { Icon, Button, ButtonTooltip } from '@evertrue/et-components';
import { getTaskTypeInfo } from '../../../utils/cadence-step-utils';

/**
 * Manual task step card for the cadence flow editor.
 * Displays the task type icon and label. Task type can be changed via the side drawer.
 *
 * When `flowLocked` is true (the cadence is active or paused) the task is fully
 * read-only: editing and deleting are disabled and a lock indicator explains why.
 * The cadence flow — including manual task action types — cannot be persisted in
 * these states, so the card must not present itself as editable.
 */
const TaskStepCard = ({ step, stepNumber, onDelete, onEdit, flowLocked }) => {
  const taskInfo = getTaskTypeInfo(step.taskType || 'other');
  const displayTitle = step.name || taskInfo.label;

  const renderDescription = () => {
    if (step.description) {
      return <span className="step-card--content-state">{step.description}</span>;
    }
    // No "add description" affordance when locked — the task is read-only.
    if (flowLocked) return null;
    return (
      <button type="button" className="step-card--add-content" onClick={onEdit}>
        + Add description
      </button>
    );
  };

  return (
    <div className={`step-card step-card--task${flowLocked ? ' step-card--locked' : ''}`}>
      <div className="step-card--header">
        <div className="step-card--title-row">
          <div className="step-card--icon-wrapper step-card--icon-task">
            <Icon icon={taskInfo.icon} />
          </div>
          <div className="step-card--title-content">
            <span className="step-card--title">{displayTitle}</span>
            {renderDescription()}
          </div>
        </div>
        {flowLocked ? (
          <ButtonTooltip
            contentWidth={280}
            tooltipPositionTop
            trigger={
              <span className="step-card--lock-indicator" aria-label="This task is locked">
                <Icon icon="lock" />
              </span>
            }
          >
            <span>
              Manual tasks can&rsquo;t be edited while the cadence is active or paused. Set the cadence to draft to make
              changes.
            </span>
          </ButtonTooltip>
        ) : (
          <div className="step-card--header-actions" onClick={(e) => e.stopPropagation()}>
            <Button type="simple" onClick={onEdit} title="Edit task">
              <Icon icon="edit" />
            </Button>
            <Button type="simple" onClick={onDelete} title="Delete step">
              <Icon icon="delete" />
            </Button>
          </div>
        )}
      </div>
      <div className="step-card--footer">
        <div className="step-card--manual-badge">
          <Icon icon={taskInfo.icon} />
          <span>Manual Task : {taskInfo.label}</span>
        </div>
        <ButtonTooltip
          contentWidth={280}
          tooltipPositionTop
          trigger={<span className="step-card--automation-help">?</span>}
        >
          <span>
            A manual task will be created and assigned to you when a constituent reaches this step. Their progress will
            be paused until you complete it.
          </span>
        </ButtonTooltip>
      </div>
    </div>
  );
};

TaskStepCard.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    taskType: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  stepNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  flowLocked: PropTypes.bool,
};

export default TaskStepCard;
